import React, { useEffect, useCallback, useState } from 'react';
import { Prompt } from 'react-router';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';

import PatientFilter from 'components/templates/rpm/patients/patient/patientFilter';
import ControlPanel from 'components/organisms/controlPanel';
import InteractiveTime from 'components/organisms/interactiveTime';
import Charts from 'components/templates/rpm/patients/patient/charts';
import PatientNameBadge from 'components/atoms/badges/patientNameBadge';
import PatientCovidStatusBadge from 'components/atoms/badges/patientCovidStatusBadge';
import PatientMobileCovidNameBadge from 'components/atoms/badges/patientMobileCovidNameBadge';
import Activity from 'components/templates/rpm/patients/patient/activity';
import UserProfile from 'components/templates/rpm/patients/patient/userProfileBlock';
import Button from 'components/atoms/button';
import PdfIcon from 'components/atoms/icons/pdfIcon';
import Medication from 'components/templates/ccm/patientDetails/medication';
import EmailArrowIcon from 'components/atoms/icons/emailArrowIcon';
import { downloadPDF, generateReportName, showResult } from 'utils';
import { isOnboardingPatient, isPatient } from 'utils/userTypeHelper';
import useUser from 'utils/useUser';
import { onboardingStatuses } from 'constants/patient_statuses';
import NotFound from 'components/atoms/notFound';
import common from 'constants/common';
import MiniNav from 'components/templates/rpm/patients/patient/miniNav';
import { isMobile } from 'utils/deviceHelper';
import { isCurrentMonthSelected } from 'utils/timeHelper';

const PatientPage = () => {
  const [isEmailButtonLoading, setIsEmailButtonLoading] = useState(false);
  const [isDownloadButtonLoading, setIsDownloadButtonLoading] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  const setLoadingState = (type, state) => {
    if (type === 'email') {
      setIsEmailButtonLoading(state);
    } else {
      setIsDownloadButtonLoading(state);
    }
  };
  const { userType } = useUser();
  const deviceType = useSelector((state) => state.user.deviceType);
  const patient = useSelector((state) => state.patient);
  const { unsavedComments, unsavedGeneralComment } = useSelector(
    (state) => state.doctor,
  );
  const { getDetails: patientDetailsLoading, getCharts: chartsLoading } =
    useSelector((state) => state.loading.effects.patient);
  const { getActivities: activitiesLoading } = useSelector(
    (state) => state.loading.effects.activity,
  );

  const { reviewed } = useSelector((state) => state.activity);

  const {
    patient: {
      applyTimeFilter,
      resetPatient,
      getDetails,
      getCharts,
      getVitalsReport,
      reset: resetActivity,
    },
    activity: { getActivities, changeReviewed },
    sms: { setTimeRange },
  } = useDispatch();

  const { patientGuid } = useParams();
  const { clinicGuid } = patient?.clinic;

  const handleSubmit = (dates) => {
    setTimeRange(dates);
    setShowMessages(true);
    applyTimeFilter(dates);
    if (isCurrentMonthSelected(dates, reviewed)) {
      changeReviewed({ patientGuid, clinicGuid });
    }
  };

  const getPatientDetails = useCallback(() => {
    getDetails(patientGuid);
  }, [patientGuid]);

  const getPatientActivity = useCallback(() => {
    if (clinicGuid) {
      getActivities({ patientGuid, clinicGuid, userType });
    }
  }, [clinicGuid, patient.dates]);

  const getPatientCharts = useCallback(() => {
    if (patient.devices.length > 0) {
      const deviceIds = patient.devices
        .map((device) => device.deviceId)
        .join(',');
      const [startTime, endTime] = patient.dates;
      getCharts({ patientGuid, devices: deviceIds, startTime, endTime });
    }
  }, [patient.devices, patient.dates]);

  const downloadReport = async (type) => {
    setLoadingState(type, true);
    const { error, data } = await getVitalsReport({ patientGuid, type });
    setLoadingState(type, false);
    if (type === 'email') {
      return showResult(error, common.submitSuccessTypes.sendReport);
    }
    if (!error && data) {
      downloadPDF(
        data,
        generateReportName(
          `vitals-report-${patientGuid}`,
          patient.dates,
          'pdf',
        ),
      );
    }
  };

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      resetPatient();
    };
  }, []);

  useEffect(() => {
    resetActivity();
    getPatientDetails(patientGuid);
  }, [getPatientDetails]);

  useEffect(() => {
    getPatientActivity();
  }, [getPatientActivity]);

  useEffect(() => {
    getPatientCharts();
  }, [getPatientCharts]);

  const showChart =
    patientDetailsLoading === true ||
    (patientDetailsLoading === false && patient.devices.length > 0);

  if (isOnboardingPatient(onboardingStatuses, patient.profile.status)) {
    return (
      <div>
        <NotFound />
      </div>
    );
  }

  return (
    <div className="patient">
      <Prompt
        when={
          !!(
            Object.keys(unsavedComments).length ||
            Object.keys(unsavedGeneralComment).length
          )
        }
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <Container>
        {!isMobile(deviceType) && (
          <>
            {!isPatient(userType) && (
              <div className="badges">
                <PatientNameBadge
                  profile={patient.profile}
                  patientDetailsLoading={patientDetailsLoading}
                />
                <PatientCovidStatusBadge
                  profile={patient.profile}
                  patientDetailsLoading={patientDetailsLoading}
                />
              </div>
            )}
          </>
        )}
        {isMobile(deviceType) && (
          <>
            {!isPatient(userType) && (
              <PatientMobileCovidNameBadge
                profile={patient.profile}
                patientDetailsLoading={patientDetailsLoading}
              />
            )}
          </>
        )}
        <Row>
          <Col xl="1" lg="1" md="1">
            <div className="patient__mini-nav patient-container">
              <MiniNav charts={patient.charts} />
            </div>
          </Col>
          <Col className="patient__main" md="11" lg="11" xl="11">
            <Row className="patient-container">
              <Col xs="12" md="8">
                <PatientFilter handleSubmitTime={handleSubmit} />
              </Col>
              {!isPatient(userType) && (
                <Col xs="12" md="4" className="report-button__wrapper">
                  <div className="report-button">
                    <Button
                      text="Email Report"
                      Icon={<EmailArrowIcon width="35" />}
                      iconAlt="emailPdfAlt"
                      onClick={() => downloadReport('email')}
                      data-cy="patient__email-download-report"
                      disabled={
                        isEmailButtonLoading ||
                        patientDetailsLoading ||
                        patient.profile.status === 'onboarding'
                      }
                    />
                  </div>
                  <div className="report-button">
                    <Button
                      text="Download Report"
                      Icon={<PdfIcon width="30" />}
                      iconAlt="pdfAlt"
                      onClick={() => downloadReport(null)}
                      data-cy="patient__download-report"
                      disabled={
                        isDownloadButtonLoading ||
                        patientDetailsLoading ||
                        patient.profile.status === 'onboarding'
                      }
                    />
                  </div>
                </Col>
              )}
            </Row>
            {!isPatient(userType) && (
              <Row className="patient-container">
                <Col xs="12" md="7">
                  <Card
                    className="card-border control-panel__card"
                    id="control-panel"
                  >
                    <CardBody>
                      <ControlPanel
                        patientGuid={patientGuid}
                        profile={patient.profile}
                        doctor={patient.doctor}
                        patientDetailsLoading={patientDetailsLoading}
                        status={patient.profile.status}
                        isRpmPanel
                      />
                    </CardBody>
                  </Card>
                </Col>
                <Col xs="12" md="5">
                  <Card className="card-border interactive-time__card">
                    <CardBody>
                      <InteractiveTime
                        patientGuid={patientGuid}
                        patient={patient}
                        patientDetailsLoading={patientDetailsLoading}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
            {showChart && (
              <Row className="patient-container" id="graphs">
                <Col xs="12">
                  <Charts
                    patient={patient}
                    patientDetailsLoading={patientDetailsLoading}
                    chartsLoading={chartsLoading}
                  />
                </Col>
              </Row>
            )}
            <Row className="patient-container" id="activity">
              <Col xs="12">
                <Activity
                  patientGuid={patientGuid}
                  patient={patient}
                  patientDetailsLoading={patientDetailsLoading}
                  activitiesLoading={activitiesLoading}
                />
              </Col>
            </Row>
            <Row className="patient-container" id="medication">
              <Col xs="12">
                <Medication patientDetailsLoading={false} canEditPage />
              </Col>
            </Row>
            <Row className="patient-container" id="user-profile">
              <Col xs="12">
                <UserProfile
                  patientGuid={patientGuid}
                  patient={patient}
                  patientDetailsLoading={patientDetailsLoading}
                  showMessages={showMessages}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PatientPage;
